import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, BASE_URL, colors as C } from "../shared/constants";
import { styles } from "../shared/styles";
import { InspectionReassignModal } from "./InspectionReassignModal";

const typeLabel = (t) => ({ monthly: "Monthly", bi_annual: "Bi-Annual", annual: "Annual" }[t] || t);
const formatDate = (d) => d
  ? new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" })
  : "—";

export function AssignWorkPage() {
  const [tab, setTab] = useState("inspections");
  const [inspections, setInspections] = useState([]);
  const [requests, setRequests] = useState([]);
  const [flowTests, setFlowTests] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const [reassigningInspection, setReassigningInspection] = useState(null);
  const [savingRequestId, setSavingRequestId] = useState(null);
  const [savingFlowTestId, setSavingFlowTestId] = useState(null);

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/inspections/`),
      axios.get(`${API}/rectification-requests/`),
      axios.get(`${API}/flow-tests/`),
      axios.get(`${BASE_URL}/auth/technicians/`),
    ])
      .then(([insRes, reqRes, flowRes, techRes]) => {
        setInspections(insRes.data);
        setRequests(reqRes.data);
        setFlowTests(flowRes.data);
        setTechnicians(techRes.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => { loadAll(); }, []);

  const techName = (t) => (t.first_name && t.last_name ? `${t.first_name} ${t.last_name}` : t.username);

  const handleRequestReassign = async (requestId, techId) => {
    setSavingRequestId(requestId);
    try {
      await axios.patch(`${API}/rectification-requests/${requestId}/`, {
        assigned_to: techId || null,
      });
      loadAll();
    } catch (err) {
      alert(err.response?.data?.detail || err.response?.data?.[0] || "Failed to assign.");
    }
    setSavingRequestId(null);
  };

  const handleFlowTestReassign = async (flowTestId, techId) => {
    setSavingFlowTestId(flowTestId);
    try {
      await axios.patch(`${API}/flow-tests/${flowTestId}/`, {
        assigned_to: techId || null,
      });
      loadAll();
    } catch (err) {
      alert(err.response?.data?.detail || err.response?.data?.[0] || "Failed to assign.");
    }
    setSavingFlowTestId(null);
  };

  const filteredInspections = unassignedOnly
    ? inspections.filter((i) => !i.assigned_to)
    : inspections;
  const filteredRequests = unassignedOnly
    ? requests.filter((r) => !r.assigned_to)
    : requests;
  const filteredFlowTests = unassignedOnly
    ? flowTests.filter((f) => !f.assigned_to)
    : flowTests;

  const unassignedInspectionCount = inspections.filter((i) => !i.assigned_to).length;
  const unassignedRequestCount = requests.filter((r) => !r.assigned_to).length;
  const unassignedFlowTestCount = flowTests.filter((f) => !f.assigned_to).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={styles.topbar}>
        <div style={styles.pageTitle}>Assign Work</div>
      </div>

      <div style={{ backgroundColor: C.white, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: "flex", paddingLeft: 24 }}>
          {[
            ["inspections", "Inspections", inspections.length, unassignedInspectionCount],
            ["requests", "Rectification Requests", requests.length, unassignedRequestCount],
            ["flowTests", "Flow Tests", flowTests.length, unassignedFlowTestCount],
          ].map(([id, label, total, unassigned]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              style={{
                background: "none", border: "none",
                borderBottom: tab === id ? `3px solid ${C.activeBlue}` : "3px solid transparent",
                padding: "14px 20px", fontSize: 14,
                fontWeight: tab === id ? 700 : 400,
                color: tab === id ? C.activeBlue : C.slate,
                cursor: "pointer", display: "flex", alignItems: "center", gap: 8,
              }}
            >
              {label}
              <span style={{ fontSize: 12, color: "#94a3b8" }}>({total})</span>
              {unassigned > 0 && (
                <span style={{
                  backgroundColor: "#fee2e2", color: C.red, fontSize: 11, fontWeight: 700,
                  padding: "2px 8px", borderRadius: 20,
                }}>
                  {unassigned} unassigned
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div style={styles.content}>
        <div style={{ marginBottom: "12px" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: C.text, cursor: "pointer" }}>
            <input type="checkbox" checked={unassignedOnly} onChange={(e) => setUnassignedOnly(e.target.checked)} />
            Show unassigned only
          </label>
        </div>

        {loading ? (
          <div style={styles.loading}>Loading...</div>
        ) : tab === "inspections" ? (
          <div style={styles.card}>
            <div style={{ ...styles.tableHeader, gridTemplateColumns: "2fr 1fr 1fr 1fr 2fr 100px" }}>
              <div>Site</div>
              <div>Section</div>
              <div>Type</div>
              <div>Date</div>
              <div>Assigned</div>
              <div></div>
            </div>
            {filteredInspections.length === 0 ? (
              <div style={styles.loading}>No inspections found.</div>
            ) : (
              filteredInspections.map((insp) => (
                <div key={insp.id} style={{ ...styles.tableRow(false), gridTemplateColumns: "2fr 1fr 1fr 1fr 2fr 100px" }}>
                  <div>
                    <div style={{ fontWeight: "500" }}>{insp.site_name}</div>
                    <div style={{ fontSize: "12px", color: "#999" }}>{insp.site_address}</div>
                  </div>
                  <div style={{ fontSize: "13px" }}>{insp.site_area_name || "Whole Site"}</div>
                  <div style={{ fontSize: "13px" }}>{typeLabel(insp.inspection_type)}</div>
                  <div style={{ fontSize: "13px" }}>{formatDate(insp.date)}</div>
                  <div style={{ fontSize: "13px" }}>
                    {insp.assigned_to_name ? (
                      <>
                        <div style={{ fontWeight: "500" }}>{insp.assigned_to_name}</div>
                        {insp.additional_techs_names?.length > 0 && (
                          <div style={{ fontSize: "12px", color: "#999" }}>+ {insp.additional_techs_names.join(", ")}</div>
                        )}
                      </>
                    ) : (
                      <span style={{ color: C.red, fontWeight: "600" }}>Unassigned</span>
                    )}
                  </div>
                  <div>
                    <button
                      onClick={() => setReassigningInspection(insp)}
                      style={{
                        background: "none", border: `1px solid ${C.border}`, borderRadius: "6px",
                        padding: "5px 10px", fontSize: "12px", cursor: "pointer", color: C.activeBlue,
                        fontWeight: "600",
                      }}
                    >
                      {insp.assigned_to_name ? "Reassign" : "Assign"}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : tab === "requests" ? (
          <div style={styles.card}>
            <div style={{ ...styles.tableHeader, gridTemplateColumns: "2fr 1fr 1fr 2fr" }}>
              <div>Site</div>
              <div>Type</div>
              <div>Status</div>
              <div>Assigned To</div>
            </div>
            {filteredRequests.length === 0 ? (
              <div style={styles.loading}>No rectification requests found.</div>
            ) : (
              filteredRequests.map((req) => (
                <div key={req.id} style={{ ...styles.tableRow(false), gridTemplateColumns: "2fr 1fr 1fr 2fr" }}>
                  <div>
                    <div style={{ fontWeight: "500" }}>{req.quote_detail?.inspection_site_name}</div>
                    <div style={{ fontSize: "12px", color: "#999" }}>{req.quote_detail?.inspection_site_address}</div>
                  </div>
                  <div style={{ fontSize: "13px" }}>{typeLabel(req.quote_detail?.inspection_type)}</div>
                  <div style={{ fontSize: "13px" }}>
                    {req.status === "completed"
                      ? <span style={{ color: C.green, fontWeight: "600" }}>Completed</span>
                      : req.status === "in_progress"
                      ? <span style={{ color: C.amber, fontWeight: "600" }}>In Progress</span>
                      : <span style={{ color: C.slate }}>Pending</span>}
                  </div>
                  <div>
                    <select
                      value={req.assigned_to || ""}
                      onChange={(e) => handleRequestReassign(req.id, e.target.value ? Number(e.target.value) : null)}
                      disabled={savingRequestId === req.id}
                      style={{
                        fontSize: "13px", padding: "5px 8px", borderRadius: "6px",
                        border: `1px solid ${req.assigned_to ? C.border : C.red}`,
                        color: req.assigned_to ? C.text : C.red,
                        maxWidth: "220px",
                      }}
                    >
                      <option value="">— Unassigned —</option>
                      {technicians.map((t) => (
                        <option key={t.id} value={t.id}>{techName(t)}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          <div style={styles.card}>
            <div style={{ ...styles.tableHeader, gridTemplateColumns: "2fr 1fr 1fr 2fr" }}>
              <div>Site</div>
              <div>Status</div>
              <div>Readings</div>
              <div>Assigned To</div>
            </div>
            {filteredFlowTests.length === 0 ? (
              <div style={styles.loading}>No flow tests found.</div>
            ) : (
              filteredFlowTests.map((ft) => (
                <div key={ft.id} style={{ ...styles.tableRow(false), gridTemplateColumns: "2fr 1fr 1fr 2fr" }}>
                  <div>
                    <div style={{ fontWeight: "500" }}>{ft.site_name}</div>
                    <div style={{ fontSize: "12px", color: "#999" }}>{ft.site_address}</div>
                  </div>
                  <div style={{ fontSize: "13px" }}>
                    {ft.status === "passed"
                      ? <span style={{ color: C.green, fontWeight: "600" }}>Passed</span>
                      : ft.status === "failed"
                      ? <span style={{ color: C.red, fontWeight: "600" }}>Failed</span>
                      : <span style={{ color: C.slate }}>In Progress</span>}
                  </div>
                  <div style={{ fontSize: "13px" }}>
                    {ft.flow_rate_ls != null ? `${ft.flow_rate_ls} L/s, ${ft.pressure_kpa} kPa` : "—"}
                  </div>
                  <div>
                    <select
                      value={ft.assigned_to || ""}
                      onChange={(e) => handleFlowTestReassign(ft.id, e.target.value ? Number(e.target.value) : null)}
                      disabled={savingFlowTestId === ft.id || ft.inspection_locked}
                      title={ft.inspection_locked ? "This flow test's report is locked and cannot be reassigned." : undefined}
                      style={{
                        fontSize: "13px", padding: "5px 8px", borderRadius: "6px",
                        border: `1px solid ${ft.assigned_to ? C.border : C.red}`,
                        color: ft.inspection_locked ? C.slate : ft.assigned_to ? C.text : C.red,
                        maxWidth: "220px",
                        opacity: ft.inspection_locked ? 0.6 : 1,
                      }}
                    >
                      <option value="">— Unassigned —</option>
                      {technicians.map((t) => (
                        <option key={t.id} value={t.id}>{techName(t)}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {reassigningInspection && (
        <InspectionReassignModal
          inspection={reassigningInspection}
          onClose={() => setReassigningInspection(null)}
          onSave={() => {
            setReassigningInspection(null);
            loadAll();
          }}
        />
      )}
    </div>
  );
}
